import { logger } from "@artis/logger";
import type { ServerClock } from "@artis/sdk";

export const RESYNC_MS = 30 * 60_000;
export const RETRY_MS = 60_000;

export const fetchNetlifyServerTime = async (url = "/.netlify/functions/server-time"): Promise<number> => {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`server-time request failed: HTTP ${res.status}`);
  const { now } = (await res.json()) as { now?: unknown };
  if (typeof now !== "number") throw new Error("server-time returned no numeric timestamp");
  return now;
};

/**
 * Main-thread time source for the chained topology (ADR-0022): asks the worker's clock
 * over Comlink, rejecting while the worker is still unsynced so the sampler retries.
 */
export const workerClockTransport =
  (clockNow: () => Promise<number | null>): (() => Promise<number>) =>
  async () => {
    const now = await clockNow();
    if (now === null) throw new Error("worker clock not synced yet");
    return now;
  };

/** Sync `clock` now, then keep it fresh: `resyncMs` after a success, `retryMs` after a failure. */
export const startServerClockSync = (
  clock: ServerClock,
  { resyncMs = RESYNC_MS, retryMs = RETRY_MS }: { resyncMs?: number; retryMs?: number } = {},
): (() => void) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;
  let failing = false;

  const schedule = (ms: number) => {
    if (stopped) return;
    timer = setTimeout(run, ms);
  };

  const run = async () => {
    try {
      await clock.sync();
      failing = false;
      const { clockSkewMs, uncertaintyMs } = clock.state();
      logger.info("Server clock synced", { clock_skew_ms: clockSkewMs, uncertainty_ms: uncertaintyMs });
      schedule(resyncMs);
    } catch (error) {
      if (!failing) logger.warn("Server clock sync failed, retrying", { error });
      failing = true;
      schedule(retryMs);
    }
  };

  void run();

  return () => {
    stopped = true;
    if (timer !== undefined) clearTimeout(timer);
  };
};
